/**
 * Password Strength Service
 * Uses zxcvbn-ts for realistic password strength estimation
 */

import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core'
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common'
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en'
import i18n from '@/i18n'

// Initialize zxcvbn options with English and common dictionaries
const options = {
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
  },
  translations: zxcvbnEnPackage.translations,
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
}

zxcvbnOptions.setOptions(options)

const passwordStrengthService = {
  /**
   * Evaluate password strength using zxcvbn algorithm
   * @param {string} password - Password to evaluate
   * @param {Array} userInputs - Optional array of user-specific words to penalize
   * @returns {Object} - Password strength evaluation result
   */
  evaluatePassword(password, userInputs = []) {
    if (!password) {
      return {
        score: 0,
        strength: 'veryWeak',
        guesses: 0,
        crackTimesSeconds: {},
        feedback: { warning: '', suggestions: [] },
        calculation_time: 0
      }
    }

    try {
      // Use zxcvbn to evaluate password
      const result = zxcvbn(password, userInputs)
      
      // Map zxcvbn score (0-4) to our strength levels
      const strengthMap = {
        0: 'veryWeak',
        1: 'weak', 
        2: 'good',
        3: 'strong',
        4: 'veryStrong'
      }

      // Convert feedback to our format
      const feedback = {
        warning: result.feedback?.warning || '',
        suggestions: result.feedback?.suggestions || []
      }

      return {
        score: result.score,
        strength: strengthMap[result.score],
        guesses: result.guesses,
        guessesLog10: result.guessesLog10,
        crackTimesSeconds: result.crackTimesSeconds,
        crackTimesDisplay: result.crackTimesDisplay,
        feedback,
        sequence: result.sequence,
        calculation_time: result.calcTime || 0
      }
    } catch (error) {
      console.warn('zxcvbn evaluation failed, using fallback:', error)
      
      // Fallback simple evaluation
      return this.fallbackEvaluation(password)
    }
  },

  /**
   * Fallback password evaluation when zxcvbn fails
   * @param {string} password - Password to evaluate
   * @returns {Object} - Basic strength evaluation
   */
  fallbackEvaluation(password) {
    let score = 0
    const feedback = { warning: '', suggestions: [] }
    
    // Basic length check
    if (password.length >= 8) score += 1
    else feedback.suggestions.push('Use at least 8 characters')
    
    // Character variety checks
    if (/[a-z]/.test(password)) score += 1
    else feedback.suggestions.push('Add lowercase letters')
    
    if (/[A-Z]/.test(password)) score += 1  
    else feedback.suggestions.push('Add uppercase letters')
    
    if (/[0-9]/.test(password)) score += 1
    else feedback.suggestions.push('Add numbers')
    
    if (/[^A-Za-z0-9]/.test(password)) score += 1
    else feedback.suggestions.push('Add special characters')
    
    // Cap score at 4
    score = Math.min(score, 4)
    
    const strengthMap = {
      0: 'veryWeak',
      1: 'weak',
      2: 'good', 
      3: 'strong',
      4: 'veryStrong'
    }
    
    if (score < 2) {
      feedback.warning = i18n.global.t('auth.setPassword.passwordTooWeak')
    }
    
    return {
      score,
      strength: strengthMap[score],
      guesses: Math.pow(10, score * 3),
      guessesLog10: score * 3,
      crackTimesSeconds: {},
      crackTimesDisplay: {},
      feedback,
      sequence: [],
      calculation_time: 0
    }
  },

  /**
   * Get password strength percentage for progress bar
   * @param {number} score - zxcvbn score (0-4)
   * @returns {number} - Percentage (0-100)
   */
  getStrengthPercentage(score) {
    const percentageMap = {
      0: 20,  // Very Weak
      1: 40,  // Weak
      2: 60,  // Good
      3: 80,  // Strong  
      4: 100  // Very Strong
    }
    return percentageMap[score] || 0
  },

  /**
   * Get CSS class for strength level
   * @param {number} score - zxcvbn score (0-4)
   * @returns {string} - CSS class name
   */
  getStrengthClass(score) {
    const classMap = {
      0: 'bg-danger',
      1: 'bg-warning',
      2: 'bg-info', 
      3: 'bg-success',
      4: 'bg-success'
    }
    return classMap[score] || 'bg-secondary'
  },

  /**
   * Get text color class for strength level
   * @param {number} score - zxcvbn score (0-4)
   * @returns {string} - CSS text color class
   */
  getStrengthTextClass(score) {
    const textClassMap = {
      0: 'text-danger',
      1: 'text-warning',
      2: 'text-info',
      3: 'text-success', 
      4: 'text-success'
    }
    return textClassMap[score] || 'text-muted'
  },

  /**
   * Check if password meets minimum strength requirements
   * @param {number} score - zxcvbn score (0-4)
   * @param {number} minScore - Minimum required score (default: 2)
   * @returns {boolean} - True if password meets requirements
   */
  meetsMinimumStrength(score, minScore = 2) {
    return score >= minScore
  },

  /**
   * Get strength level as localized text
   * @param {string} strength - Strength level key
   * @param {Function} t - Translation function
   * @returns {string} - Localized strength text
   */
  getLocalizedStrength(strength, t) {
    if (!t) return strength
    
    const strengthKeys = {
      veryWeak: 'auth.setPassword.passwordStrength.veryWeak',
      weak: 'auth.setPassword.passwordStrength.weak',
      good: 'auth.setPassword.passwordStrength.good', 
      strong: 'auth.setPassword.passwordStrength.strong',
      veryStrong: 'auth.setPassword.passwordStrength.veryStrong'
    }
    
    return t(strengthKeys[strength] || strengthKeys.veryWeak)
  },

  /**
   * Add custom dictionary words (useful for site-specific terms)
   */
  addCustomWords() {
    // This would require extending the dictionary
    // For now, these can be passed as userInputs to evaluatePassword
    console.log('Custom words should be passed as userInputs to evaluatePassword()')
  }
}

export default passwordStrengthService 